import { IFirestoreListenerData, IFirestoreQueryData, IFirestoreRealtimeData } from '../core/types';

import { BaseFirestorePlugin } from '../core/BaseFirestorePlugin';
import Vue from 'vue';

export interface IFirestoreStore {
  queries: IFirestoreQueryData[];
  currentQuery?: IFirestoreQueryData;
  lastQuery?: IFirestoreQueryData;
  listeners: Record<string, number>;
  listenerHistory: IFirestoreListenerData[];
  realtimeUpdates: IFirestoreRealtimeData[];
}

export class FirestoreStateManager extends BaseFirestorePlugin {
  private store: IFirestoreStore;
  private activeListeners: Map<string, Set<string>> = new Map();

  constructor() {
    super('firestore-state-manager', 'Firestore State Manager');
    this.store = Vue.observable<IFirestoreStore>({
      queries: [],
      listeners: {},
      listenerHistory: [],
      realtimeUpdates: []
    });
  }

  beforeQuery(queryData: Omit<IFirestoreQueryData, 'duration' | 'resultCount' | 'error'>): void {
    this.store.currentQuery = queryData as IFirestoreQueryData;
  }

  afterQuery(queryData: IFirestoreQueryData): void {
    this.store.queries.unshift(queryData);
    if (this.store.queries.length > 100) {
      this.store.queries.length = 100;
    }
    this.store.lastQuery = queryData;
  }

  afterListenerAdded(listenerData: IFirestoreListenerData): void {
    // Add to active listeners using the unique ID
    if (!this.activeListeners.has(listenerData.collection)) {
      this.activeListeners.set(listenerData.collection, new Set());
    }
    this.activeListeners.get(listenerData.collection)?.add(listenerData.id);

    // Update the store
    this.store.listeners[listenerData.collection] =
      (this.activeListeners.get(listenerData.collection)?.size || 0);

    // Add to history
    this.store.listenerHistory.unshift(listenerData);
    if (this.store.listenerHistory.length > 50) {
      this.store.listenerHistory.length = 50;
    }
  }

  afterListenerRemoved(listenerData: IFirestoreListenerData): void {
    // Remove from active listeners using the unique ID
    this.activeListeners.get(listenerData.collection)?.delete(listenerData.id);

    // If no more listeners for this collection, delete the Set
    if (this.activeListeners.get(listenerData.collection)?.size === 0) {
      this.activeListeners.delete(listenerData.collection);
      delete this.store.listeners[listenerData.collection];
    } else {
      // Update the count
      this.store.listeners[listenerData.collection] =
        (this.activeListeners.get(listenerData.collection)?.size || 0);
    }

    // Add to history
    this.store.listenerHistory.unshift({
      ...listenerData,
      type: 'removed'
    });
    if (this.store.listenerHistory.length > 50) {
      this.store.listenerHistory.length = 50;
    }
  }

  afterRealtimeDataReceived(realtimeData: IFirestoreRealtimeData): void {
    this.store.realtimeUpdates.unshift(realtimeData);
    if (this.store.realtimeUpdates.length > 50) {
      this.store.realtimeUpdates.length = 50;
    }
  }

  getStore(): IFirestoreStore {
    return this.store;
  }

  getActiveListenersCount(): number {
    // Get count from our more accurate structure
    let count = 0;
    this.activeListeners.forEach(listeners => {
      count += listeners.size;
    });

    return count;
  }

  getAllActiveListeners(): Record<string, number> {
    // Convert Map+Set to a simple object for UI display
    const result: Record<string, number> = {};
    this.activeListeners.forEach((listeners, collection) => {
      result[collection] = listeners.size;
    });

    return result;
  }
}
